import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Enter a valid email"),
  website: z.string().min(3, "Enter your website or N/A"),
  bottleneck: z.string().min(10, "Tell us a bit more (min 10 chars)"),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must agree to the privacy policy to continue",
  }),
});

type FormValues = z.infer<typeof schema>;

type Props = { onSubmitted?: () => void };

const FreeAuditForm: React.FC<Props> = ({ onSubmitted }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", website: "", bottleneck: "", consent: false },
    mode: "onTouched",
  });

  const [step, setStep] = useState(0);
  const steps: { fields: (keyof FormValues)[]; title: string }[] = [
    { fields: ["name", "email"], title: "About you" },
    { fields: ["website"], title: "Your business" },
    { fields: ["bottleneck", "consent"], title: "Your biggest bottleneck" },
  ];
  const isLast = step === steps.length - 1;

  const next = async () => {
    const fields = steps[step].fields;
    const valid = await form.trigger(fields, { shouldFocus: true });
    if (valid) setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = form.handleSubmit(async (values) => {
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: values.name,
          email: values.email,
          website: values.website,
          bottleneck: values.bottleneck,
          consent: values.consent,
          formType: 'audit',
        },
      });

      if (error) {
        console.error('Error sending audit form:', error);
        throw error;
      }

      toast.success("Thanks! I'll be in touch about your free audit soon.", {
        description: `${values.name} • ${values.email}`,
      });
      
      form.reset();
      onSubmitted?.();
    } catch (error: any) {
      console.error('Audit form submission error:', error);
      toast.error("Unable to submit request. Please try again or email me at hello@aimontclair.com");
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-base text-cream-muted">
            Step {step + 1} of {steps.length}
          </p>
        </div>

        {step === 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full name</FormLabel>
                  <FormControl>
                    <Input placeholder="Alex Johnson" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@company.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {step === 1 && (
          <div className="grid gap-4">
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company website</FormLabel>
                  <FormControl>
                    <Input placeholder="https://example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {step === 2 && (
          <div className="grid gap-4">
            <FormField
              control={form.control}
              name="bottleneck"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What's your biggest bottleneck?</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={5}
                      placeholder="Describe the tasks you'd love to automate…"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="consent"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal">
                      I agree to the{" "}
                      <Link to="/privacy" target="_blank" className="text-primary hover:underline">
                        Privacy Policy
                      </Link>
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <Button type="button" variant="outline" onClick={back} disabled={step === 0}>
            Back
          </Button>
          {!isLast ? (
            <Button type="button" onClick={next}>
              Next
            </Button>
          ) : (
            <Button type="submit">Submit</Button>
          )}
        </div>
      </form>
    </Form>
  );
};

export default FreeAuditForm;
